import { BaseService } from "../BaseService";
import { AuthService } from "../auth/auth.service";
import { environment } from '../../../../src/environments/environment';
import { HttpClient } from "@angular/common/http";
import { Router } from '@angular/router';
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { ApiResult } from "../../models/ApiResult";
import { EventModel } from "../../models/api/EventModel";
import { EventFindResultModel } from "../../models/api/EventFindResultModel";
import { Mini_EventModel } from "../../models/api/Mini_EventModel";
import { Mini_EventFindResultModel } from "../../models/api/Mini_EventFindResultModel";

export abstract class EventBaseService extends BaseService {

    private urlPart: string = 'Event';

    constructor(private http: HttpClient, private router: Router, private auth: AuthService) {
        super(http, router, auth);
        this.apiSegmentUrl = this.urlPart;
    };

    /// <summary>
    /// FIND: Retrieves a list of Event records from the database
    /// /Event
    /// </summary>
    public findEvent(page: number = 0, size: number = 0, orderBy: string = "", orderDirection: string = "ASC", filter: string = "", eventstatusid: number = 0): Observable<EventFindResultModel>
    {
        // Find Event Records

        const url = `${super.getBaseUrl()}?page=${page}&size=${size}&orderBy=${orderBy}&orderDirection=${orderDirection}&filter=${filter}&eventstatusid=${eventstatusid}`;

        if (!environment.production)
            console.log("Find Event: Calling " + url);

        return this.http.get<EventFindResultModel>(url)
            .pipe(
                map(next => {
                    let o: EventFindResultModel = Object.assign(new EventFindResultModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// GET: Retrieves a single Event record from the database
    /// /Event/{eventid}
    /// </summary>
    /// <param name="eventid"></param>
    /// <returns></returns>
    public getEvent(eventid: number): Observable<EventModel>
    {

        // Get Single Event Record

        const url = `${super.getBaseUrl()}/${eventid}`;

        if (!environment.production)
            console.log("Get Event: Calling " + url);

        return this.http.get<EventModel>(url)
            .pipe(
                map(next => {
                    let o: EventModel = Object.assign(new EventModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// GET: Retrieves a single Mini_Event record from the database
    /// /Mini_Event/{eventid}
    /// </summary>
    /// <param name="eventid"></param>
    /// <returns></returns>
    public getEventMiniRecord(eventid: number): Observable<Mini_EventModel>
    {

        // Get Single Mini_Event Record

        const url = `${super.getBaseUrl()}/mini/${eventid}`;

        if (!environment.production)
            console.log("Get Event: Calling " + url);

        return this.http.get<Mini_EventModel>(url)
            .pipe(
                map(next => {
                    let o: Mini_EventModel = Object.assign(new Mini_EventModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// POST: Adds a new mini Mini_Event record to the database
    /// /Event/mini/{eventid}
    /// </summary>
    /// <param name="eventid"></param>
    /// <returns></returns>
    public createEventMini(newRecord: Mini_EventModel): Observable<Mini_EventModel>
    {
        // Add Single Mini_Event Record

        const url = `${super.getBaseUrl()}/mini`;

        if (!environment.production)
            console.log("Create Event:Calling " + url);

        return this.http.post<Mini_EventModel>(url, newRecord)
            .pipe(
                map(next => {
                    let o: Mini_EventModel = Object.assign(new Mini_EventModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// PUT: Updates an existing Mini_Event record in the database
    /// /Event/mini/{eventid}
    /// </summary>
    /// <param name="eventid"></param>
    /// <returns></returns>
    public updateEventMini(eventid: number, updatedRecord: Mini_EventModel): Observable<Mini_EventModel>
    {
        // Update Single Mini_Event Record

        const url = `${super.getBaseUrl()}/mini/${eventid}`;

        if (!environment.production)
            console.log("Update Mini_Event: Calling " + url);

        return this.http.put<Mini_EventModel>(url, updatedRecord)
            .pipe(
                map(next => {
                    let o: Mini_EventModel = Object.assign(new Mini_EventModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public updateEventMiniRecord(updatedRecord: Mini_EventModel): Observable<Mini_EventModel>
    {
        return this.updateEventMini(updatedRecord.eventID, updatedRecord);
    }

    /// <summary>
    /// DELETE: Deletes an existing Mini_Event record from the database
    /// /Event/mini/{eventid}
    /// </summary>
    /// <param name="eventid"></param>
    /// <returns></returns>
    public deleteEventMini(eventid: number): Observable<ApiResult>
    {
        // Delete Single Mini_Event Record

        const url = `${super.getBaseUrl()}/mini/${eventid}`;

        if (!environment.production)
            console.log("Delete Mini_Event: Calling " + url);

        return this.http.delete<ApiResult>(url)
            .pipe(
                map(next => {
                    let o: ApiResult = new ApiResult();
                    o.success = true;
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public deleteEventMiniRecord(deleteRecord: Mini_EventModel): Observable<ApiResult>
    {
        // Delete Single Mini_Event Record
        return this.deleteEventMini(deleteRecord.eventID);
    }

    /// <summary>
    /// POST: Adds a new Event record to the database
    /// /Event/{eventid}
    /// </summary>
    /// <param name="eventid"></param>
    /// <returns></returns>
    public createEvent(newRecord: EventModel): Observable<EventModel>
    {
        // Add Single Event Record

        const url = `${super.getBaseUrl()}`;

        if (!environment.production)
            console.log("Create Event:Calling " + url);

        return this.http.post<EventModel>(url, newRecord)
            .pipe(
                map(next => {
                    let o: EventModel = Object.assign(new EventModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// PUT: Updates an existing Event record in the database
    /// /Event/{eventid}
    /// </summary>
    /// <param name="eventid"></param>
    /// <returns></returns>
    public updateEvent(eventid: number, updatedRecord: EventModel): Observable<EventModel>
    {
        // Update Single Event Record

        const url = `${super.getBaseUrl()}/${eventid}`;

        if (!environment.production)
            console.log("Update Event: Calling " + url);

        return this.http.put<EventModel>(url, updatedRecord)
            .pipe(
                map(next => {
                    let o: EventModel = Object.assign(new EventModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public updateEventRecord(updatedRecord: EventModel): Observable<EventModel>
    {
        return this.updateEvent(updatedRecord.eventID, updatedRecord);
    }

    /// <summary>
    /// DELETE: Deletes an existing Event record from the database
    /// /Event/{eventid}
    /// </summary>
    /// <param name="eventid"></param>
    /// <returns></returns>
    public deleteEvent(eventid: number): Observable<ApiResult>
    {
        // Delete Single Event Record

        const url = `${super.getBaseUrl()}/${eventid}`;

        if (!environment.production)
            console.log("Delete Event: Calling " + url);

        return this.http.delete<ApiResult>(url)
            .pipe(
                map(next => {
                    let o: ApiResult = new ApiResult();
                    o.success = true;
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public deleteEventRecord(deleteRecord: EventModel): Observable<ApiResult>
    {
        // Delete Single Event Record
        return this.deleteEvent(deleteRecord.eventID);
    }

}
